import { useState } from 'react';
import debounce from 'lodash/debounce';
import { Flex, type TableColumnsType } from 'antd';
import { useNavigate } from 'react-router';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd, MdSearch, MdOutlineCopyAll } from 'react-icons/md';
import { PageMeta } from '@app/components/meta/Meta';
import PageHeader from '@app/components/page/PageHeader';
import { PageTitle } from '@app/components/page/PageTitle';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageLayout } from '@app/components/page';
import { TextField } from '@app/components/form-fields/TextField';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { StyledTable } from '@app/components/table/Table';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import useFlows from '@app/hooks/flows/useFlows';
import type { AIFlowListParams, AIFlowListItem } from '@app/services/aiFlow.types';
import FlowDrawer from './components/drawers/FlowDrawer';
import useModal from '@app/hooks/useModal';
import DeleteFlowModal from './components/modals/DeleteFlowModal';
import { StyledAlert } from '@app/components/alert/Alert';

const AIFlowsList = () => {
    const navigate = useNavigate();
    const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
    const [params, setParams] = useState<AIFlowListParams>({
        page: 1,
        per_page: 10,
        search: undefined,
        order_by: undefined,
        order_dir: undefined,
    });
    const { isFetching, data } = useFlows(params);

    const modalDelete = useModal<AIFlowListItem>({
        initialData: undefined,
    });

    const columns: TableColumnsType<AIFlowListItem> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            render: (_, record) => record.name ?? '-',
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            sorter: true,
            render: (_, record) => record.id ?? '-',
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            render: (_, record) =>
                record.created_at ? fn.formatDate(record.created_at, 'DD/MM/YYYY (HH:mm)') : '-',
        },
        {
            title: 'Updated At',
            dataIndex: 'updated_at',
            render: (_, record) =>
                record.updated_at ? fn.formatDate(record.updated_at, 'DD/MM/YYYY (HH:mm)') : '-',
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            width: 300,
            fixed: 'right',
            render: (_, record) => (
                <StyledButtonGroup justify="flex-end">
                    <StyledButton
                        icon={<MdOutlineCopyAll />}
                        size="small"
                        color="blue"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                                url.adminPortal.internalAdmin.agentFlows.builderAdd +
                                    `?id=${record.id}`
                            );
                        }}>
                        Clone
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlinePencil />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                                url.adminPortal.internalAdmin.agentFlows.builderEdit(record.id)
                            );
                        }}>
                        Edit
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlineTrash />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            modalDelete.open(record);
                        }}>
                        Delete
                    </StyledButton>
                </StyledButtonGroup>
            ),
        },
    ];

    return (
        <>
            <PageMeta title="AI Agent Flows" />
            <StyledAlert title="BETA" />
            <PageHeader>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="AI Agent Flows" />
                        <PageIntro>
                            <p>Manage questionnaire flows for AI Agents</p>
                        </PageIntro>
                    </div>
                    <div>
                        <StyledButtonGroup>
                            <StyledButton
                                type="primary"
                                color="blue"
                                icon={<MdAdd size={20} />}
                                onClick={() =>
                                    navigate(url.adminPortal.internalAdmin.agentFlows.builderAdd)
                                }>
                                Add New
                            </StyledButton>
                        </StyledButtonGroup>
                    </div>
                </Flex>
            </PageHeader>
            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <div className="form-fields-wrapper">
                            <TextField
                                placeholder="Search Flows..."
                                prefix={<MdSearch size={20} />}
                                onChange={debounce((e) => {
                                    const value = e.target.value;
                                    setParams((prev) => ({
                                        ...prev,
                                        search: value,
                                    }));
                                }, 500)}
                            />
                        </div>
                    </Flex>
                    <br />
                    <StyledTable<AIFlowListItem>
                        dataSource={data?.data ?? []}
                        columns={columns}
                        rowKey={({ id }) => id}
                        loading={isFetching}
                        pagination={{
                            current: params.page,
                            pageSize: params.per_page,
                            total: data?.total ?? 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedFlowId(record.id),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>
            <FlowDrawer
                open={Boolean(selectedFlowId)}
                onClose={() => setSelectedFlowId(null)}
                id={selectedFlowId!}
                name={data?.data?.find((flow) => flow.id === selectedFlowId)?.name ?? ''}
            />
            <DeleteFlowModal
                open={modalDelete.isOpen}
                data={modalDelete.data!}
                onCancel={() => modalDelete.reset()}
            />
        </>
    );
};

export default AIFlowsList;
